<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import {
  inputsStep4Maritimo,
  inputsStep4Paqueteria,
  inputsStep2AR,
  inputsStep3AR,
  inputsStep4AR,
} from './utils/inputsForm';
import { useToast } from '../../composables/useToast';
import { useMessage } from '../../composables/useMessage';
import { validationCUIT } from '../../helpers/validationCUIT';
import { IFinalCalculation } from '../../helpers/finalCalculationAR';
import { wrongDepartureDateTime } from '../../helpers';
import {
  createOrUpdateClient,
  createPolicyForTrygerLatam,
  createPolicyPackage,
  createPolicyMaritime,
  findClientByTributaryCode,
  findTypesContainers,
  findCompaniesGPS,
  getPricing,
  findCountries
} from '../../repositories/landing.repository';
import GeoRefArgentinaRepository, {
  IProvince,
} from '../../repositories/geoRefArgentina.repository';
import moment from 'moment';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();

const props = defineProps({
  dataForm1: {
    type: Object,
    required: true,
  },
});

const fieldsStep2: any = ref(inputsStep2AR());
const fieldsStep3: any = ref(inputsStep3AR());
const fieldsStep4: any = ref(inputsStep4AR());
const inputsHidden = ref<boolean>(true);
const dataForm: any = ref({});
const refFormClientData = ref<any>(null);
const refFormTravelData = ref<any>(null);
const loading = ref<boolean>(false);
const provincesARG = ref<IProvince[]>([]);
const finalCalculation = ref<IFinalCalculation | null>(null);

const emit = defineEmits<{
  (e: 'firstView'): void;
}>();

onMounted(async () => {
  if (!props.dataForm1) {
    inputsHidden.value = true;
    return emit('firstView');
  }
  if (props.dataForm1.typePolicieTransport === 'Paqueteria') {
    fieldsStep4.value = inputsStep4Paqueteria();
  }
  if (props.dataForm1.typePolicieTransport === 'Maritimo') {
    fieldsStep4.value = inputsStep4Maritimo();
  }
});

onMounted(async () => {
  const provinces = await GeoRefArgentinaRepository.getprovinces();
  provincesARG.value = provinces;
  const comapaniesGPS = await findCompaniesGPS();
  const typesContainers = await findTypesContainers();
  const countries = await findCountries();

  fieldsStep3.value = fieldsStep3.value.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
    }
    if (field.key === 'province') {
      field.options = provinces;
    }
    return field;
  });
  fieldsStep4.value = fieldsStep4.value.map((field: any) => {
    if (field.key === 'destinationProvince') {
      field.options = provinces;
    }
    if (field.key === 'originProvince') {
      field.options = provinces;
    }
    return field;
  });
  fieldsStep4.value = fieldsStep4.value.map((field: any) => {
    if (field.key === 'containerType') {
      field.options = typesContainers;
    }
	if (field.key === 'countryDeparture') {
      field.options = countries;
    }
    if (field.key === 'countryArrival') {
      field.options = countries;
    }
    return field;
  });
});

onUnmounted(() => {
  fieldsStep3.value = inputsStep3AR().map((e) => (e.value = null));
  fieldsStep4.value = inputsStep4AR().map((e) => (e.value = null));
  inputsHidden.value = true;
  dataForm.value = {};
});

const handleOnSubmitCUIT = async (e: any) => {
  const checkCuil = validationCUIT(e.items.cuit);

  if (
    !e.isFormValid ||
    !checkCuil.valid ||
    checkCuil.type === 'Tipo desconocido'
  ) {
    return (fieldsStep2.value[0].errors = ['CUIT es invalido']);
  }
  //   loading.value = true;
  const { client } = await findClientByTributaryCode(e.items.cuit);

  if (client) {
    dataForm.value = { client };
  }

  if (client) {
    const updatesClientInputs = [
      { key: 'tradeName', value: client.tradeName },
      { key: 'email', value: props?.dataForm1?.email ?? null },
      { key: 'phone1', value: client.phone1 },
      {
        key: 'companyGPSId',
        value: {
          id: client.companyGPS?.id,
          nombre: client.companyGPS?.description,
        },
      },
      { key: 'address', value: client.address },
      { key: 'province', value: { nombre: client.regionProvince } },
      { key: 'city', value: { nombre: client.location } },
      { key: 'postCode', value: client.postCode },
    ];

    updateFieldsInArray(fieldsStep3.value, updatesClientInputs, null);
  }
  const updatesTravelInputs = [
    {
      key: 'assetId',
      value: props?.dataForm1?.asset?.nombre ?? null,
    },
    {
      key: 'indemnificationLimit',
      value: props?.dataForm1?.indemnificationLimit ?? null,
    },
    {
      key: 'indemnificationLimit-calc',
      value: `${props?.dataForm1?.endPrice} + IVA`,
    },
  ];

  updateFieldsInArray(fieldsStep4.value, updatesTravelInputs, null);
  inputsHidden.value = false;
  fieldsStep3.value.find(
    (field: any) => field.key === 'tradeName',
  ).validations.rules.max = checkCuil.type === 'Persona Jurídica' ? 40 : 20;
  if (!client) {
    const updatesClientInputs = [
      { key: 'email', value: props?.dataForm1?.email ?? null },
    ];
    updateFieldsInArray(fieldsStep3.value, updatesClientInputs, null);

    dataForm.value = { client: { rfc: e.items.rfc } };
  } else {
    finalCalculation.value = await getPricing(
      props?.dataForm1?.indemnificationLimit,
      e.items.cuit,
      client.regionProvince!,
    );
  }
};

const handleGetCities = async () => {
  const { items } = await refFormClientData.value.getValues();
  const preservedValues = {
    tradeName: items.tradeName,
    email: items.email,
    phone1: items.phone1,
    companyGPSId: items.companyGPSId,
    address: items.address,
    province: items.province,
  };

  if (items.province.id !== 99) {
    const cities = await GeoRefArgentinaRepository.getCities(items.province.id);
    await refFormClientData.value.setValue({ key: 'city', value: null });
    updateFieldsInArray(
      fieldsStep3.value,
      [
        {
          key: 'city',
          type: 'select',
          options: cities,
          value: null,
          customClass: 'inputSelect',
        },
      ],
      preservedValues,
    );
  } else {
    await refFormClientData.value.setValue({ key: 'city', value: null });
    updateFieldsInArray(
      fieldsStep3.value,
      [
        {
          key: 'city',
          type: 'text',
          value: null,
          customClass: 'inputLanding',
        },
      ],
      preservedValues,
    );
  }

  finalCalculation.value = await getPricing(
    props?.dataForm1?.indemnificationLimit,
    dataForm.value.client.cuit,
    items.province.nombre,
  );
};

const handleSelectChange = async (e: any) => {
  const updateFields: {
    key: string;
    disabled?: boolean;
    'validations.rules.required'?: boolean;
    options?: any[];
  }[] = [];

  const updateCommonFields = (key: string, isEnabled: boolean) => {
    updateFields.push(
      { key, disabled: !isEnabled },
      { key, 'validations.rules.required': isEnabled },
    );
  };

  const handleCityUpdate = async (cityKey: string) => {
    const cities = await GeoRefArgentinaRepository.getCities(e.value.id);
    await refFormTravelData.value.setValue({ key: cityKey, value: null });

    const updateCityFields = [
      {
        key: cityKey,
        type: e.value.id !== 99 ? 'select' : 'text',
        options: e.value.id !== 99 ? cities : undefined,
        disabled: false,
      },
    ];
    updateFieldsInArray(fieldsStep4.value, updateCityFields, null);
  };

  switch (e.input.key) {
    case 'box-trailer':
      updateCommonFields('trailerPlate', !!e.value.id);
      break;

    case 'originProvince':
      await handleCityUpdate('originCity');
      return;

    case 'destinationProvince':
      await handleCityUpdate('destinationCity');
      return;

    default:
      ['stop1', 'stop2', 'stop3'].forEach((stop) =>
        updateCommonFields(stop, !!e.value.id),
      );
      break;
  }

  updateFieldsInArray(fieldsStep4.value, updateFields, null);
};

const updateFieldsInArray = (
  array: any,
  updates: any,
  preservedValues: any,
) => {
  for (const update of updates) {
    const field = array.find((field: any) => field.key === update.key);
    if (field) {
      if ('type' in update) {
        field.type = update.type;
      }
      if ('options' in update) {
        field.options = update.options;
      }
      if ('value' in update) {
        field.value = update.value;
      }
      if ('disabled' in update) {
        field.disabled = update.disabled;
      }
    }
  }

  if (preservedValues) {
    for (const key in preservedValues) {
      const field = array.find((field: any) => field.key === key);
      if (field) {
        field.value = preservedValues[key];
      }
    }
  }
};

const handleOnSubmitPolicies = ({ items, isFormValid }: any) => {
  if (props.dataForm1.typePolicieTransport === 'Paqueteria') {
    handleOnSubmitPoliciesPaqueteria({ items, isFormValid });
  } else if (props.dataForm1.typePolicieTransport === 'Terrestre') {
    handleOnSubmitPoliciesTerrestre({ items, isFormValid });
  } else {
    handleOnSubmitPoliciesMaritime({ items, isFormValid });
  }
};

const handleCreateUpdateClient = async () => {
  try {
    const { items: clientData, isFormValid } = await refFormClientData.value.getValues();

    if (!isFormValid) {
      return showMessage(t('nueva_operacion.nueva_contratacion_ar.el_formulario_debe_estar_completo'), 'error');
    }

    if ((clientData.tradeName?.split(' ')?.length ?? 0) < 2) {
      return showMessage(t('nueva_operacion.nueva_contratacion_ar.debe_proporcionar_al_menos_un_nombre'), 'error');
    }

    // Formato de la data para el request
    const createOrUpdateClientData = {
      tradeName: clientData.tradeName,
      address: clientData.address,
      email: clientData.email,
      phone1: clientData.phone1,
      location: clientData.city?.nombre ?? clientData.city,
      postCode: clientData.postCode,
      cuit: dataForm.value.client?.cuit,
      regionProvince: clientData.province?.nombre,
      companyGPSId: clientData.companyGPSId?.id,
      urlDomain: 'AR',
    };

    return await createOrUpdateClient(dataForm.value.client?.id, createOrUpdateClientData, 'AR');
  } catch (error: any) {
    showMessage(`${t('global.ocurrio_un_error')}: ${error.message || 'desconocido'}`, 'error');
    throw error;
  }
};

const handleOnSubmitPoliciesTerrestre = async (formData: any) => {
  const refFormTravelDataItems = formData.items;
  const refFormTravelDataValid = formData.isFormValid;

  const formClientData = await refFormClientData.value.getValues();
  const refFormClientDataItems = formClientData.items;
  const refFormClientDataValid = formClientData.isFormValid;

  if (!refFormClientDataValid || !refFormTravelDataValid) {
    return showMessage(t('nueva_operacion.nueva_contratacion_ar.el_formulario_debe_estar_completo'), 'error');
  }

  if (wrongDepartureDateTime(refFormTravelDataItems.startDate, refFormTravelDataItems.startTime, 'cl')) {
    return showMessage(t('nueva_operacion.nueva_contratacion_ar.la_hora_de_salida_no_puede_ser_menor'), 'error');
  }

  if (refFormClientDataItems.tradeName.split(' ').length < 2) {
    return showMessage(t('nueva_operacion.nueva_contratacion_ar.debe_proporcionar_al_menos_un_nombre'), 'error');
  }

  const travelStartDate = new Date(`${refFormTravelDataItems.startDate}T${refFormTravelDataItems.startTime}`);
  const travelEndDate = new Date(`${refFormTravelDataItems.endDate}T${refFormTravelDataItems.endTime}`);
  const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  const assetId = props.dataForm1.asset.id;
  const parseProvince = refFormClientDataItems.province.nombre === 'Ciudad Autónoma de Buenos Aires' ? 'Buenos Aires' : refFormClientDataItems.province.nombre;

  const createPolicyData: any = {
    tradeName: refFormClientDataItems.tradeName,
    assetId,
    phone1: refFormClientDataItems.phone1,
    typeOfMerchandise: refFormTravelDataItems.typeOfMerchandise,
    indemnificationLimit: refFormTravelDataItems.indemnificationLimit,
    originRegionProvince: refFormTravelDataItems.originProvince.nombre,
    destinationRegionProvince: refFormTravelDataItems.destinationProvince.nombre,
    originLocation: refFormTravelDataItems.originCity.nombre,
    destinationLocation: refFormTravelDataItems.destinationCity.nombre ?? refFormTravelDataItems.destinationCity,
    stop1: refFormTravelDataItems.stop1,
    stop2: refFormTravelDataItems.stop2,
    stop3: refFormTravelDataItems.stop3,
    trailerPlate: refFormTravelDataItems.trailerPlate,
    truckPlate: refFormTravelDataItems.truckPlate,
    driverName: refFormTravelDataItems.driverName,
    documentDescription: refFormTravelDataItems.documentDescription,
    driverCellphone: refFormTravelDataItems.driverCellphone,
    driverEmail: refFormTravelDataItems.driverEmail,
    travelStartDate: fechaISOstart,
    travelEndDate: fechaISOend,
    hasStops: refFormTravelDataItems.stops.nombre,
    companyGPSId: refFormClientDataItems.companyGPSId.id,
    document: refFormTravelDataItems.document?.length ? refFormTravelDataItems.document[0].image : null,
    clientName: refFormClientDataItems.tradeName,
    tributaryCode: dataForm.value.client.cuit,
    address: refFormClientDataItems.address,
    postalCode: refFormClientDataItems.postCode,
    location: refFormClientDataItems.city.nombre,
    regionProvince: parseProvince,
    email: refFormClientDataItems.email,
    endPrice: finalCalculation?.value?.primaTotal,
    isAuth: true,
  };

  try {
    loading.value = true;
    const client = await handleCreateUpdateClient();
    const urlForPayment = await createPolicyForTrygerLatam(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData
    );

    loading.value = false;
    if (urlForPayment) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_ar.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error) {
    loading.value = false;
    showMessage(t('global.ocurrio_un_error'), 'error');
  }
};

const handleOnSubmitPoliciesPaqueteria = async (formData: any) => {

  const travelData = formData.items;
  const isTravelValid = formData.isFormValid;
  const { items: clientData } = await refFormClientData.value.getValues();
  try {
    if (!isTravelValid) {
      return showMessage(t('nueva_operacion.nueva_contratacion_ar.el_formulario_debe_estar_completo'), 'error');
    }

	const { indemnificationLimit, packageNumbers } = travelData;
    const { companyGPSId, tradeName } = clientData;

    loading.value = true;

    const client = await handleCreateUpdateClient();

    const packageNumbersCleaned = packageNumbers
      ?.split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e !== '') ?? [];

    const createPolicyData = {
      tradeName,
      cuit: dataForm.value.client?.cuit,
      indemnificationLimit,
      companyGPSId: companyGPSId?.id,
      packageNumbers: JSON.stringify(packageNumbersCleaned),
	  isAuth: true,
    };

    const newPolicyId = await createPolicyPackage(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'AR',
    );

    if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_ar.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
    showMessage(`${t('global.ocurrio_un_error')}: ${error.message || 'desconocido'}`, 'error');
    throw error; // Propaga el error para manejarlo en la función llamadora
  } finally {
    loading.value = false;
  }
};

const handleOnSubmitPoliciesMaritime = async (formData: any) => {
  const travelData = formData.items;
  const isTravelValid = formData.isFormValid;
  const { items: clientData } = await refFormClientData.value.getValues();

  if (!isTravelValid) {
    return showMessage(t('nueva_operacion.nueva_contratacion_ar.el_formulario_debe_estar_completo'), 'error');
  }

  if (travelData.destinationCityZipCodeIdCalc === 'CP INVALIDO' || travelData.sourceCityZipCodeIdCalc === 'CP INVALIDO') {
    return showMessage(t('nueva_operacion.nueva_contratacion_ar.codigo_postal_no_valido'), 'error');
  }

  try {
    loading.value = true;
    const client = await handleCreateUpdateClient();
    const assetId = props.dataForm1.asset.id;

    const createPolicyData = {
      ...travelData,
      ...clientData,
      assetId,
      cuit: dataForm.value.client.cuit,
      companyGPSId: clientData.companyGPSId.id,
      typeContainerId: travelData.containerType.id,
	  countryIdDeparture: travelData.countryDeparture.id,
	  countryIdArrival: travelData.countryArrival.id,
	  isAuth: true,
    };

    const newPolicyId = await createPolicyMaritime(client.data.client.id, client.data.manager.id, createPolicyData, 'AR');

    loading.value = false;
    if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_ar.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
    showMessage(`${t('global.ocurrio_un_error')}. ${error.message}`, 'error');
    loading.value = false;
  }
};

const handleAllowEndDate = async (e: any) => {
  const isTerrestre = props.dataForm1.typePolicieTransport === 'Terrestre';
  const key = isTerrestre ? 'endDate' : 'travelEndDate';

  const field = fieldsStep4.value.find((field: any) => field.key === key);
  if (field) {
    field.disabled = !e.value;
    field.min = new Date(`${e.value}T00:00:00`);
  }

  await refFormTravelData.value.setValue({ key, value: null });
};
</script>

<template>
  <Layout
  	:title="t('nueva_operacion.nueva_contratacion_ar.nueva_contratacion')" 
	:textColor="'color-secondary'"
  >
    <template #layout>
      <Form
        :inputs="fieldsStep2"
        @onSubmit="handleOnSubmitCUIT"
        v-if="inputsHidden"
      >
        <template #buttons>
          <div class="d-flex">
            <CButton background="background-blue" style="font-size: 1rem">
              <template #content> {{ t('nueva_operacion.nueva_contratacion_ar.comprobar_cuit') }} </template>
            </CButton>
          </div>
        </template>
      </Form>
      <template v-if="!inputsHidden">
        <h4 class="color-pink">
          {{ t('nueva_operacion.nueva_contratacion_ar.datos_del_cliente') }} ({{ t('nueva_operacion.nueva_contratacion_ar.cuit') }}: {{ dataForm?.client?.cuit ?? null }})
        </h4>
        <Form
          ref="refFormClientData"
          :inputs="fieldsStep3"
          @select="handleGetCities"
        ></Form>
        <h4 class="my-3 color-pink">{{ t('nueva_operacion.nueva_contratacion_ar.datos_del_viaje') }}</h4>
        <Form
          ref="refFormTravelData"
          :inputs="fieldsStep4"
          @onSubmit="handleOnSubmitPolicies"
          @select="handleSelectChange"
          @datePicker="handleAllowEndDate"
        >
         <template #buttons>
            <div class="my-2" v-if="messageHTML" v-html="messageHTML" />
            <div v-if="finalCalculation" class="container p-0 my-2">
              <b-card class="rounded-4">
                <h2 class="text-center text-primary">
					{{ t('nueva_operacion.nueva_contratacion_ar.calculo_de_prima_fina_y_pago') }}
                </h2>
                <b-row class="mt-3">
                  <b-col>
                    <b-list-group>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-light"
                      >
                        <span><strong>{{ t('nueva_operacion.nueva_contratacion_ar.monto_imponible') }}:</strong></span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.montoImponible }}</span
                        >
                      </b-list-group-item>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('nueva_operacion.nueva_contratacion_ar.taxes') }}</span
                        >
                      </b-list-group-item>

                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{t('nueva_operacion.nueva_contratacion_ar.iva')}}:</span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.IVA }}</span
                        >
                      </b-list-group-item>

                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-primary text-white"
                      >
                        <span><strong>{{t('nueva_operacion.nueva_contratacion_ar.prima_total')}}:</strong></span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.primaTotal }}</span
                        >
                      </b-list-group-item>
                    </b-list-group>
                  </b-col>
                </b-row>
              </b-card>
            </div>
            <CButton
              class="p-4 w-100 px-2"
              background="background-blue"
              style="font-size: 1.5rem"
            >
              <template #content>
                <div>
                  <BSpinner v-if="loading" />
                  <span v-else>{{t('nueva_operacion.nueva_contratacion_ar.registrar_poliza')}}</span>
                </div>
              </template>
            </CButton>
          </template>
        </Form>
      </template>
    </template>
  </Layout>
</template>
